<div class="flex flex-col gap-y-10">
  @if (store.positionsNotice(); as notice) {
    <!-- 🔴 A SLEEVE REPORTING A BALANCE WITH NO HOLDINGS IS ITS OWN CASE, checked FIRST and on
         every request type. Eclipse has been seen returning no holdings for a sleeve that is
         really there while the trade team runs bulk updates (AndyP, 2026-09-25) -- so this
         advisor is not being told a fact about their account, they are being told what we can
         currently see, and asked to come back. Every other notice here describes a settled state.

         ⚠️ It takes the ORANGE treatment on every type, not just a distribution. It is the one
         notice here that says something may be WRONG, and the grey informational panel would read
         as "this is how your account is". -->
    @if (store.customHoldingsUnavailable()) {
      <p class="px-15 py-10 text-sm border rounded border-orange-lighter bg-orange-lightest text-orange-darker">
        {{ notice }}
      </p>
    } @else if (store.isDistribution()) {
      <!-- ⚠️ These notices are written for a reallocation ("there are no hand-traded positions
           to reallocate"). For a distribution the sleeve being ABSENT is not an explanation to
           dismiss but a real constraint -- there is nothing here to raise cash from -- so it says
           so and points back at the models.

           🟢 Only reached when the sleeve is genuinely absent or genuinely empty, since the
           branch above took the case where one reports a balance it cannot show. Saying "this
           account has no Custom Holdings positions" over a sleeve we simply could not read was
           the bug that split fixes. -->
      <p class="px-15 py-10 text-sm border rounded border-orange-lighter bg-orange-lightest text-orange-darker">
        This account has no Custom Holdings positions or custodial cash, so the whole distribution
        has to come from the model sleeves on the previous step.
      </p>
    } @else {
      <p class="px-15 py-10 text-sm border rounded border-gray-lighter bg-gray-lightest text-gray-dark">
        {{ notice }}
      </p>
    }
  } @else {

  <!-- The instruction and the way back out of it, on one line.

       ⚠️ The button is `flex-shrink-0` and the paragraph is not: the text is what should reflow
       as the modal narrows. Without it the button is the thing that gives ground, and "Reset"
       wraps or clips long before the sentence beside it needs to. -->
  <div class="flex items-start justify-between gap-x-20">
    <!-- ⚠️ `lede()`, not the sentence written out. The reallocation wording -- "unchecked
         positions move to the Custom Holdings sleeve" -- is ACTIVELY WRONG for a distribution:
         nothing moves, and telling an advisor their untouched holdings are being re-sleeved
         describes a restructure they did not request. -->
    <p class="text-sm text-gray">{{ lede() }}</p>
    <!-- ⚠️ The SAME button as Reset on Allocate to Models, down to the classes and the icon:
         solid mint, `is-sm`, `refresh`, disabled when there is nothing to undo. Two controls
         that do the same thing on two steps of one wizard have to look the same, or the advisor
         reads them as different actions.

         Outlined was tried here first, to mark it as secondary against Next. The models step
         had already been through that and rejected it for the reason recorded there: beside a
         filled button an outline reads as the DISABLED state of one rather than as a lighter
         control -- and its real disabled state is what carries that distinction.

         ⚠️ DISABLED until there is something to clear, which is most of the safety this needs.
         On an untouched step it is visibly inert rather than a live control that does nothing,
         and it cannot be hit before any work exists to lose. -->
    <button type="button" class="flex items-center button is-mint is-sm gap-x-5 flex-shrink-0"
      [disabled]="!store.stepOneHasEntries()"
      title="Put every selection and amount on this step back as it loaded"
      (click)="reset()">
      <i class="material-icons" style="font-size: 1.6rem" aria-hidden="true">refresh</i>
      Reset
    </button>
  </div>

  <!-- /!\ THE GREEN "CUSTODIAL CASH / TAKE $" PANEL WAS HERE and is gone (2026-09-17).

       It offered the same money as the Cash Positions table below -- both read
       `data.cashPositions`, which is every holding the loader split out on `isCash` -- so the
       step asked for one decision twice, in two shapes.

       /!\ THE TABLE IS THE ONE THAT SURVIVES, and not only for consistency with the
       reallocation wizard. The panel wrote `cashTakenControl`, a single total that reached
       `unappliedValue` and NOTHING ELSE; the table writes `cashForm`, which becomes
       `releasedCash` in the payload -- one row per position, with its real ticker and Eclipse
       security id. Keeping the panel instead would have left a distribution's cash draw
       moving the arithmetic on screen while filing nothing, which is the exact gap
       ACMTECH-981 was opened to close for reallocations.

       A distribution now draws cash the same way a reallocation releases it. See
       `cashTaken` in the store for how the figure is still named for what it means here. -->

  <!-- ⚠️ THE ONLY STEP THAT CAN SUPPLY MONEY, and it was the only one that never mentioned
       needing any. The buy step has said "sell more on the Review/Sell Positions step" for weeks;
       the advisor arrived here knowing they had to act and found no figure, no instruction, and a
       cash panel that did not know the request was short.

       ⚠️ NO FIGURE HERE ANY MORE. It carried the amount for a while, on the reasoning that the
       advisor could not work it out for themselves -- true before the strip reported overspends,
       which it now does, signed and in red, on every step. Repeating it in the bar directly below
       said the same number twice in two formats.

       ⚠️ It still names more than one place, because the answer is not necessarily here. Trimming
       a buy or a model allocation resolves this just as well, and may be what the advisor wants.

       ⚠️ NOT a blocker. `positionsStepBlockedReason` is untouched and Next stays enabled: being
       over-committed mid-build is an ordinary state, and the steps that follow are where it is
       usually resolved. Submit is what refuses -- see `submitBlockers`.

       Same treatment as the buy step's banner and step 4's, down to the classes: three red
       messages about one condition should not look like three kinds of problem. -->
  @if (store.isOverCommitted()) {
    <p class="px-15 py-10 text-sm border rounded border-red-light bg-red-lightest text-red-dark">
      More funds are committed than the account has available. Correct the allocations from other
      positions, models or set-aside cash.
    </p>
  }

  <!-- CASH IN THE SLEEVE, above the securities and in its own table.
       Hidden entirely when the sleeve holds none.

       ⚠️ Its own table, not rows among the securities. A cash line has no share count and no
       price -- Orion reports both as null -- so it would sit in that table with four empty
       columns and a "Shares to Sell" box it cannot use. It is also a different decision: not
       "sell this" but "release this", which needs a dollar figure rather than a quantity.

       ⚠️ And it is opt-IN. Every dollar of this used to be added to the investable balance
       automatically, so the wizard spent the advisor's cash for them and the only way back was
       the Protected Cash step. Nothing here reaches the pool until it is ticked. -->
  @if (store.data()?.cashPositions?.length) {
    <div class="flex flex-col gap-y-5">
      <!-- ⚠️ THE POLICY ITSELF, stated once, beside the heading it governs rather than on
           every row. The per-row icon says what happened to THAT balance; this says why the rule
           exists at all, which an advisor needs once and not five times.

           Native `title`, matching every other explanatory icon in this wizard -- there is no
           tooltip component here, and introducing one for a single string would be the larger
           change. It reads on hover and on focus, not on click. -->
      <!-- ⚠️ NO "REQUIRED CASH RESERVE" TOTAL HERE, and it was built and removed (AndyP,
           2026-09-18). It was bound to `cashTargetHold`, which is CLAMPED to the cash on hand --
           so on an account below target it printed the balance back as the requirement, reading
           "met exactly" when the account was short and every dollar was locked.

           The honest figure needs the unclamped requirement as a separate computed, and a way to
           say "short by X". Neither is worth a header total: the per-row Available column already
           answers what can be spent, which is the question actually being asked here. -->
      <!-- ⚠️ THE HEADING CARRIES THE TOOLTIP as well as the icon, and `w-fit` is what keeps that
           honest: a block-level h4 spans the full modal width, so hovering empty space far to the
           right of the words would pop the policy. Shrunk to its content, the target is the words
           and the icon and nothing else. -->
      <!-- ⚠️ THE POLICY MARK IS DISTRIBUTION-AWARE. A distribution is exempt from the 1% target
           (see `cashTargetHold`), so every row's Available equals its balance and nothing has been
           held back. Leaving the warning icon and its tooltip would promise an explanation for
           something that did not happen -- the same reasoning that already keeps the icon off
           untouched ROWS, applied to the heading that sits above them.

           🟢 Plain `h4` in that case, not a reworded one: there is no policy to state, so there is
           nothing to say. `cursor-help` goes with it, or the words invite a hover that pops
           nothing. -->
      @if (store.isDistribution()) {
        <h4 class="flex items-center w-fit text-sm font-bold gap-x-5 text-gray-darkest">Cash Positions</h4>
      } @else {
        <h4 class="flex items-center w-fit text-sm font-bold cursor-help gap-x-5 text-gray-darkest"
          [title]="cashPolicyTooltip">
          Cash Positions
          <i class="material-icons cursor-help text-orange-darker" style="font-size: 1.5rem"
            tabindex="0"
            [title]="cashPolicyTooltip"
            [attr.aria-label]="cashPolicyTooltip">warning_amber</i>
        </h4>
      }
      <!-- ⚠️ NO padded, tinted wrapper. It had one, and its horizontal padding pushed this
           table's first column 15px right of the securities table below -- so the two checkbox
           columns did not line up, which reads as two unrelated widgets rather than two halves
           of one list. Both tables now start at the same left edge, and the headings do the
           separating the tint was doing. -->
      <table class="w-full text-sm border-collapse">
        <thead>
          <!-- ⚠️ The HEADER carries the emphasis in both tables, not the rows.
               Uppercase, tracked and on its own tinted ground, so each section has a firm top
               edge -- which matters more now the tinted wrapper is gone and the two tables sit
               on the same left edge.

               Deliberately GREY, not mint. Mint means "the request" everywhere in this wizard --
               the inputs, the strip's terms, the rows below that are being acted on -- and a
               mint table head would spend that meaning on furniture. -->
          <tr class="text-xs tracking-wide uppercase text-gray-dark bg-gray-lightest">
            <!-- ⚠️ THE WHOLE USE COLUMN IS GONE (AndyP, 2026-09-24 on a distribution, 2026-09-25
                 everywhere). The amount IS the decision -- a row with a figure in it is a row
                 being used -- so a tick in front of it was a second click that said nothing the
                 figure did not, and it made a half-state reachable (ticked, no amount) that
                 needed its own red border.

                 ⚠️ THE SELECTION EDGE MOVED TO TICKER rather than the column being kept empty to
                 hold it. An empty column indents every row by its own padding, and reads as a
                 column whose heading failed to load. -->
            <th class="p-8 text-left border-b border-gray-light">Ticker</th>
            <th class="p-8 text-left border-b border-gray-light">Description</th>
            <th class="p-8 text-right border-b border-gray-light">Value</th>
            <!-- ⚠️ WHAT IS LEFT, not what was taken. The hold fills CUSTODIAL_CASH first and
                 then the largest balance, while the table stays sorted by ticker -- so a per-row
                 figure is what stops two rows being capped differently with nothing on screen to
                 explain it. Stating the usable half rather than the withheld half answers the
                 question the advisor actually has: how much of this can I spend.

                 🔴 AND IT HAS NOTHING TO SAY ON A DISTRIBUTION, which is exempt from the 1% target
                 -- every row's Available would simply repeat its Value. A column that restates the
                 one beside it invites the reader to look for the difference (AndyP, 2026-09-24). -->
            @if (!store.isDistribution()) {
              <th class="p-8 text-right border-b border-gray-light">Available</th>
            }
            <th class="p-8 text-right border-b border-gray-light">Amount to Use</th>
            <!-- Unlabelled, matching the securities table's controls column: a button whose own
                 text says what it does needs no heading over it. -->
            <th class="w-1 p-8 border-b border-gray-light"></th>
          </tr>
        </thead>
        <tbody>
          <!-- ⚠️ `$index` into `cashGroup`, exactly as the securities table does with
               `positionGroup`. The controls are paired to `data().cashPositions` by index and
               nothing else, so this list must not be re-ordered here. -->
          @for (c of store.data()?.cashPositions ?? []; track c.ticker; let i = $index) {
            <!-- Same rule as the securities rows below: mint marks the cash being used. -->
            <tr [formGroup]="store.cashGroup(i)" class="border-b border-gray-lightest"
              [class.bg-mint-lightest]="store.cashGroup(i).controls.use.value"
              [class.opacity-60]="store.cashRowCeilings()[i] <= 0">
              <!-- ⚠️ The 4px edge is always THERE, only its colour changes. Adding the border on
                   selection would shift every cell in the row 4px right as the figure lands. -->
              <td class="p-8 font-semibold border-l-4 text-gray-darkest"
                [class.border-transparent]="!store.cashGroup(i).controls.use.value"
                [class.border-mint-dark]="store.cashGroup(i).controls.use.value">
                {{ c.ticker }}
              </td>
              <td class="p-8">{{ c.description }}</td>
              <td class="p-8 text-right" style="font-variant-numeric: tabular-nums">
                {{ c.marketValue | currency }}
              </td>
              <!-- ⚠️ The icon rides on the rows the target actually TOUCHED. On an untouched row
                   Available is simply the balance, and an icon there would promise an explanation
                   for something that did not happen. -->
              @if (!store.isDistribution()) {
              <td class="p-8 text-right" style="font-variant-numeric: tabular-nums">
                @if (store.cashRowHeld(i) > 0) {
                  <!-- ⚠️ THE FIGURE CARRIES THE TOOLTIP TOO, not just the icon -- the number is
                       what the advisor is actually looking at when they wonder about it, and
                       making them find the 1.5rem icon first is a worse answer than none. -->
                  <span class="inline-flex items-center justify-end cursor-help gap-x-5 text-orange-darker"
                    [title]="cashAvailabilityTooltip">
                    {{ store.cashRowCeilings()[i] | currency }}
                    <i class="material-icons cursor-help" style="font-size: 1.5rem" tabindex="0"
                      [title]="cashAvailabilityTooltip"
                      [attr.aria-label]="c.ticker + ': ' + cashAvailabilityTooltip">warning_amber</i>
                  </span>
                } @else {
                  {{ store.cashRowCeilings()[i] | currency }}
                }
              </td>
              }
              <td class="p-8 text-right">
                <!-- The $ sits outside the box, as it does on Protected Cash and the buy step.
                     appTwoDecimals because this is money; the share boxes below deliberately
                     have no such directive. appCommitOnEnter because the control is
                     `updateOn: 'blur'` and Enter is the other way people say "done".

                     ⚠️ A `border-red-dark` marked a row ticked but still at 0, and is GONE with
                     `cashRowNeedsAmount` (AndyP, 2026-09-25): `use` is derived from this very
                     figure now, so a row cannot be in use and empty at the same time. -->
                <div class="flex items-center justify-end gap-x-5">
                  <span class="text-gray-dark">$</span>
                  <input type="number" min="0" [max]="store.cashRowCeilings()[i]" formControlName="amount"
                    appSelectOnFocus appCommitOnEnter appTwoDecimals
                    class="w-120 text-right input is-sm no-spinner"
                    [attr.aria-label]="'Amount of ' + c.ticker + ' to use'" />
                </div>
              </td>
              <!-- ⚠️ ONE BUTTON, NEVER TWO. "Use the lot" and "use just enough" are different
                   answers to the same question, so showing both would make the advisor work out
                   which applies -- `cashRowCoversRemaining` decides, and only the useful one is
                   drawn (AndyP, 2026-09-24).

                   🟢 ACTIONS, NOT TOGGLES, though they borrow the SELL ALL styling. SELL ALL is a
                   mode -- it locks the row's box while it is on -- whereas these fill the box and
                   leave it editable, which is the point: "use for remaining" is a starting figure
                   the advisor may still adjust. Nothing reads as pressed because nothing stays on.

                   ⚠️ USE FOR REMAINING is distribution-only, inside a column that is not: it
                   means "cover what is still to be raised", and a reallocation has no such
                   figure. USE ALL generalises, so this column appears whenever ticks are gone. -->
              <!-- ⚠️ `text-right`, and `w-1` on the header. The cell is the last column of a
                   `w-full` table, so it absorbs all the slack -- which left the button floating in
                   the middle of a wide empty cell with the table's right edge far beyond it
                   (AndyP, 2026-09-24). Right-aligning puts it on that edge.

                   🟢 The column is deliberately NOT fixed-width: USE ALL and USE FOR REMAINING are
                   different lengths, so pinning it would either clip the longer or pad the
                   shorter. Amount to Use shifts a little as they swap, which is accepted. -->
              <td class="p-8 text-right">
                <!-- ⚠️ TOGGLES, like the per-row SELL ALL they sit opposite -- so a filled row says
                     which button filled it, and pressing again empties it. `store` decides which
                     of the two is drawn; a PRESSED button stays on screen whatever the arithmetic
                     then says, or it would vanish on its own click with no way to undo. -->
                <!-- 🔴 ONE BUTTON, AND ONLY WHEN IT SETTLES THE REQUEST. A USE ALL sat beside this
                     and was removed (AndyP, 2026-09-24): cash is a LAST RESORT, and a one-click
                     "take the lot" invited sweeping the account's cash into a request that could
                     have been raised by selling what it needed.

                     ⚠️ A row that cannot cover the remainder therefore offers nothing. The box is
                     still there -- typing a figure is a considered act, which is the point. -->
                @if (store.showUseRemainingOnCashRow(i)) {
                  <button type="button" class="button is-sm is-mint whitespace-nowrap"
                    [class.is-inverted]="store.cashRowFill(i) !== 'remaining'"
                    [class.is-outlined]="store.cashRowFill(i) !== 'remaining'"
                    [attr.aria-pressed]="store.cashRowFill(i) === 'remaining'"
                    [title]="store.cashRowFill(i) === 'remaining'
                      ? 'Covering what is left to distribute — press to clear this row'
                      : 'Take just enough from this row to cover what is left to distribute'"
                    (click)="store.useRemainingOnCashRow(i)">
                    USE FOR REMAINING
                  </button>
                } @else if (store.showUseAvailableOnCashRow(i)) {
                  <!-- ⚠️ "AVAILABLE", NOT "ALL", and the word is load-bearing. The figure is the
                       row's ceiling AFTER the 1% cash target, so on a reallocation it is genuinely
                       less than the balance in the Value column beside it. USE ALL would promise
                       that balance and hand over less. -->
                  <button type="button" class="button is-sm is-mint whitespace-nowrap"
                    [class.is-inverted]="store.cashRowFill(i) !== 'available'"
                    [class.is-outlined]="store.cashRowFill(i) !== 'available'"
                    [attr.aria-pressed]="store.cashRowFill(i) === 'available'"
                    [title]="store.cashRowFill(i) === 'available'
                      ? 'Using everything this row can release — press to clear this row'
                      : 'Use everything this row can release'"
                    (click)="store.useAvailableOnCashRow(i)">
                    USE AVAILABLE
                  </button>
                }
              </td>
            </tr>
          }
        </tbody>
        <!-- ⚠️ ONE LINE, as it was. A three-line breakdown -- total, target hold, available --
             was built here first and removed (AndyP, 2026-09-18): the per-row Available column
             already answers what the advisor can spend, and restating it as a deduction made the
             foot argue the same point twice. The policy is explained once, by the heading's icon.

             The totals row keeps its own column count: Available is a per-row figure, so there is
             nothing to total under it. -->
        <tfoot>
          <tr class="font-semibold border-t border-gray-light text-gray-darkest">
            <!-- Ticker + Description, the two columns before Value. -->
            <td class="p-8 text-xs font-normal tracking-wide uppercase text-gray border-l-4 border-transparent"
              colspan="2">
              Total cash
            </td>
            <td class="p-8 text-right" style="font-variant-numeric: tabular-nums">
              {{ store.totalCash() | currency }}
            </td>
            <!-- The spacer under Available, which a distribution does not have. -->
            @if (!store.isDistribution()) {
              <td></td>
            }
            <td class="p-8 text-right" style="font-variant-numeric: tabular-nums">
              <!-- What this block contributes, and nothing about where it leaves the request --
                   the strip above owns that. Saying it in both places is what made the funding
                   step's validation read as two separate problems. -->
              Using {{ store.cashReleased() | currency }}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  }

  <div class="flex flex-col gap-y-5">
    <!-- ⚠️ THE BULK CONTROL MOVED OUT OF THE HEADER ROW (AndyP, 2026-09-24). It was a select-all
         checkbox in the first column, hidden on a distribution because ticking every row said
         nothing about how much of each to sell. With the per-row ticks gone the question changes:
         "sell everything" is a real instruction an advisor gives, and a labelled button is how it
         should be given -- a checkbox in a column header asks the reader to infer its scope.

         🟢 A TOGGLE, like the per-row SELL ALL it drives, so the pressed state says the whole
         account is going. It routes through `setRowSellAll` for every sellable row, which is why
         each row then looks exactly as it would have done clicking them one at a time. -->
    <div class="flex items-center justify-between gap-x-10">
      <h4 class="text-sm font-bold text-gray-darkest">Non-Cash Positions</h4>
      @if (store.showSellAllPositionsButton()) {
        <button type="button" class="button is-sm is-mint"
          [class.is-inverted]="!store.allPositionsSellingAll()"
          [class.is-outlined]="!store.allPositionsSellingAll()"
          [attr.aria-pressed]="store.allPositionsSellingAll()"
          [title]="store.allPositionsSellingAll()
            ? 'Every position is being sold in full — press to clear them all'
            : 'Sell every position in full'"
          (click)="store.sellAllPositions()">
          SELL ALL POSITIONS
        </button>
      }
    </div>
    <div class="w-full overflow-x-auto">
      <table class="w-full text-sm border-collapse">
      <thead>
        <tr class="text-xs tracking-wide uppercase text-gray-dark bg-gray-lightest">
          <!-- ⚠️ THE WHOLE SELL COLUMN WENT WITH THE TICKS (AndyP, 2026-09-24 on a distribution,
               2026-09-25 everywhere). What remained was a heading over an empty column -- and the
               4px selection edge, which Ticker carries instead. An empty column kept only for a
               border indents every row by its own padding.

               🟢 SELL ALL POSITIONS above the table replaced the select-all: a labelled button
               states its scope where a checkbox in a column header asks the reader to infer it.
               store.toggleSellAll() is untouched -- Reset and the SMA commit still clear through
               it -- and still exercised by the specs. -->
          <th class="p-8 text-left border-b border-gray-light">Ticker</th>
          <th class="p-8 text-left border-b border-gray-light">Description</th>
          <th class="p-8 text-right border-b border-gray-light">Shares</th>
          <th class="p-8 text-right border-b border-gray-light">Price</th>
          <th class="p-8 text-right border-b border-gray-light">Total Cost</th>
          <th class="p-8 text-right border-b border-gray-light">% of Acct</th>
          <th class="p-8 text-right border-b border-gray-light">Market Value</th>
          @if (showGainLoss) {
            <th class="p-8 text-right border-b border-gray-light">Unrealized G/L</th>
          }
          <th class="p-8 text-right border-b border-gray-light">Shares to Sell</th>
          <!-- ⚠️ THE CONTROLS GET THEIR OWN COLUMN so "Shares to Sell" sits over the INPUT rather
               than over an input-plus-buttons group (AndyP, 2026-09-22). Sharing one cell put the
               header above the middle of a group whose widest part is the box, so the label read
               as belonging to the buttons as much as to the figure.

               Deliberately unlabelled: two controls whose own text and tooltips say what they do
               need no heading, and "Actions" is a word that tells a reader nothing.

               ⚠️ `w-1` so the column is only as wide as what is in it -- the same treatment the
               cash table's button column has. Without it this column claims a share of the table's
               width whether or not anything is drawn, and the two tables end on different edges
               the moment the controls hide (AndyP, 2026-09-24). -->
          <th class="w-1 p-8 border-b border-gray-light"></th>
        </tr>
      </thead>
      <tbody>
        @for (p of store.positions(); track p.ticker; let i = $index) {
          <!-- ⚠️ Emphasis marks the DECISION, not the default -- and it used to be the other
               way round. `bg-mint-lightest` fired on `inSleeve`, which is true of every row the
               advisor has not touched, so the whole table arrived tinted and the tint said
               nothing. Worse, a row they DID act on then took `opacity-70` and faded: the one
               state worth seeing was the quietest on screen.

               Now a row being sold takes the mint ground and a mint edge, and everything else
               stays plain. Same language as the strip and the Review page's own cash lines:
               mint is the request. -->
          <!-- ⚠️ THE WHOLE ROW CARRIES THE ⚠️'s MESSAGE (AndyP, 2026-09-18). The icon sits in the
               first column beside the checkbox, so on a wide modal an advisor reading the ticker
               or the value is a long way from the one thing that explains the mark. A row-wide
               hover target means they find it wherever they happen to be looking.

               ⚠️ Same branch order as the icon below, and it has to be: the two conditions are
               opposites -- cannot be sold, versus sells fine but the proceeds are held -- so a
               row matching the first must not be described by the second. Both read the same two
               strings off the component, so the row and the icon cannot drift apart.

               🟢 Null, not an empty string, on an ordinary row: `[attr.title]` drops the
               attribute entirely, where "" would leave a title that pops an empty tooltip box in
               some browsers. -->
          <tr [formGroup]="store.positionGroup(i)"
            class="border-b border-gray-lightest"
            [class.bg-mint-lightest]="p.selected"
            [class.opacity-60]="!p.sellable"
            [class.cursor-help]="!p.sellable || p.requiresReview"
            [attr.title]="!p.sellable ? notSellableTooltip : (p.requiresReview ? reviewProceedsTooltip : null)">
            <!-- ⚠️ The 4px edge is always THERE, only its colour changes. Adding the border on
                 selection would shift every cell in the row 4px right as the figure lands.

                 ⚠️ TICKER CARRIES BOTH JOBS now the tick cell is gone -- the edge and the two
                 warning icons. The icons cannot simply be dropped: one marks a row that CANNOT be
                 sold, the other a row that sells fine but whose proceeds are held, and both are
                 the only explanation on screen for either. -->
            <td class="p-8 font-semibold border-l-4 text-gray-darkest"
              [class.border-transparent]="!p.selected"
              [class.border-mint-dark]="p.selected">
              <div class="flex items-center gap-x-5">
                <span>{{ p.ticker }}</span>
                @if (!p.sellable) {
                  <!-- ⚠️ UNREACHABLE TODAY: nothing sets sellable false. The asset-category rule
                       was removed, the securityTypeId rule that replaced it was removed in turn
                       (everything is sellable now), and what took its place restricts where the
                       PROCEEDS may go rather than the trade. Kept as the visible half of the lock
                       mechanism, which is now on its third cycle.

                       The message no longer names the category. It used to, and that is how the
                       removed rule surfaced - as "M positions cannot be sold in this request",
                       because Orion returns abbreviations in that field as well as names. A
                       future rule should say why in its own terms rather than echoing Orion. -->
                  <i class="material-icons cursor-help text-orange-darker" style="font-size: 1.5rem"
                    [title]="notSellableTooltip"
                    [attr.aria-label]="p.ticker + ' cannot be sold'">warning_amber</i>
                } @else if (p.requiresReview) {
                  <!-- ⚠️ THE SAME ICON, THE OPPOSITE MEANING, and the two must not be merged.
                       The branch above marks a row that CANNOT be sold; this one marks a row that
                       can be sold perfectly well -- its share input is live -- whose proceeds are
                       then held rather than freed to spend.

                       Hung off `sellable` instead, it would disable the very rows this change
                       exists to let advisors sell.

                       Amber rather than red: nothing here is wrong or blocked. The sale goes
                       through, the money is simply not available to this request. -->
                  <i class="material-icons cursor-help text-orange-darker" style="font-size: 1.5rem"
                    [title]="reviewProceedsTooltip"
                    [attr.aria-label]="p.ticker + ' requires review; proceeds are set aside'">warning_amber</i>
                }
              </div>
            </td>
            <td class="p-8">{{ p.description }}</td>
            <!-- ⚠️ BOLD, unlike the other figures on the row (AndyP, 2026-09-24). Shares and
                 Market Value are the two the advisor reads to decide what to sell -- how much
                 there is, and what it is worth. Price, cost basis and % of account are context.
                 Weighting all of them equally is what made the row a wall of numbers. -->
            <td class="p-8 font-semibold text-right text-gray-darkest">{{ p.shares != null ? (p.shares | number) : '—' }}</td>
            <td class="p-8 text-right">{{ p.price != null ? (p.price | currency) : '—' }}</td>
            <td class="p-8 text-right">{{ p.totalCost != null ? (p.totalCost | currency) : 'N/A' }}</td>
            <td class="p-8 text-right">{{ p.pctOfAccount | number: '1.2-2' }}%</td>
            <td class="p-8 font-semibold text-right text-gray-darkest">{{ p.marketValue | currency }}</td>
            @if (showGainLoss) {
              <td class="p-8 text-right"
                [class.text-green-dark]="p.gainLoss != null && p.gainLoss >= 0"
                [class.text-red-dark]="p.gainLoss != null && p.gainLoss < 0"
                [class.text-gray-light]="p.gainLoss == null">
                @if (p.gainLoss != null) {
                  {{ p.gainLoss >= 0 ? '+' : '−' }}{{ (p.gainLoss < 0 ? -p.gainLoss : p.gainLoss) | currency }}
                } @else {
                  N/A
                }
              </td>
            }
            <td class="p-8 text-right">
              @if (p.sellable) {
                <!-- ⚠️ `step="1"` IS ONLY HALF THE RULE. Every type sells whole shares now, so
                     the browser should not offer a fraction as valid in the first place -- but
                     `step` is advisory and pasting sidesteps it entirely, which is why the real
                     floor lives in the store's clamp. This stops the control and the control's
                     value disagreeing on screen.

                     ⚠️ A `border-red-dark` and an "Enter a share count" message hung here off
                     `needsShares`, and both are GONE with it (AndyP, 2026-09-25): `selected` is
                     derived from this figure, so a row cannot be marked to sell and empty. -->
                <input type="number" min="0" [max]="p.shares"
                  step="1" formControlName="sharesToSell"
                  appSelectOnFocus appCommitOnEnter
                  class="w-full text-right input is-sm no-spinner"
                  [attr.aria-label]="'Shares to sell for ' + p.ticker" />
              } @else {
                <!-- ⚠️ `h-28` MATCHES `.input.is-sm`, which is what the branch above renders
                     (styles.input.scss:367). Bare text here left a locked row visibly shorter than
                     the ones around it, which was noticed and disliked the last time these rows
                     could be locked at all (AndyP, 2026-09-22) — and it matters more now that a
                     distribution locks every reviewable position, so several short rows can sit
                     together in the middle of a full-height table.

                     The height token rather than the `.input` class itself: reusing the class
                     would drag in its border and background and need both overridden to stop a
                     read-only label looking like an empty control. -->
                <span class="flex items-center justify-end h-28 text-gray-light">N/A</span>
              }
            </td>

            <!-- The calculator, then SELL ALL: two ways to fill the box beside them. One for "I
                 know the dollars", one for "I want the lot". Together they are what makes rows
                 resting at 0 workable rather than tedious.

                 ⚠️ items-START, not items-center: an errored row grows taller than the buttons, and
                 centring would float them down away from the box they fill.

                 🟢 EVERY TYPE HAS THIS CELL NOW. It was distribution-only while that was the only
                 type whose rows rested at 0; all three do (AndyP, 2026-09-25). -->
            <td class="p-8">
              <div class="flex items-start gap-x-5" style="min-height: 2.8rem">
                @if (canSellWholeRow(p)) {
                  <!-- ⚠️ HIDDEN ON ANY ROW A BUTTON HAS DECIDED, not just a locked one. The test
                       was `!isRowSellAll(i)`, which covered SELL ALL because its box is locked and
                       the calculator could only have written a figure that was ignored. SELL FOR
                       REMAINING and SELL 1 SHARE leave the box live, so the calculator kept sitting
                       beside a row that already had an answer -- a third way to fill one field
                       (AndyP, 2026-09-24).

                       ⚠️ AND GONE ENTIRELY once the distribution is covered, alongside the
                       buttons -- a finished request should not still be offering ways to add to
                       it. The share box stays live, so a row can still be edited by hand, which is
                       what brings everything back the moment it goes short again.

                       🟢 Its slot stays reserved either way, so pressing a button does not shuffle
                       the controls in the row. And the calculator is still the way in on an
                       untouched row: using it writes a share count, which clears the fill mode and
                       brings the button back unpressed. -->
                  <!-- ⚠️ THE RESERVED SLOT GOES WITH THE CONTROLS. It exists so pressing a button
                       does not shuffle the row while the advisor is working -- but on a covered
                       request nothing can appear in it at all, and 2.8rem of held-open space is
                       what stopped the shares box lining up with the cash box above it. -->
                  @if (!store.distributionSatisfied()) {
                  <span class="flex items-center justify-center"
                    style="height: 2.8rem; width: 2.8rem; min-width: 2.8rem">
                    @if (store.showCalculatorOnRow(i) && canCalculateShares(p)) {
                      <!-- Drawn inline, taking the mint token through fill="currentColor", one path
                           with fill-rule="evenodd" so the screen and keys are HOLES rather than
                           white shapes -- they have to survive a tinted row, and a selected row
                           here is bg-mint-lightest. See the buy step for the geometry notes. -->
                      <button type="button"
                        class="flex items-center justify-center px-0 text-mint-darker hover:opacity-75"
                        style="height: 2.8rem; width: 2.8rem; min-width: 2.8rem"
                        title="Work out the shares from a dollar amount"
                        [attr.aria-label]="'Work out shares to sell from an amount for ' + p.ticker"
                        (click)="openShareCalculator(i, p)">
                        <svg viewBox="0 0 16 24" style="height: 2rem; width: 1.34rem"
                          fill="currentColor" fill-rule="evenodd" aria-hidden="true" focusable="false">
                          <path d="M3 0 H13 A3 3 0 0 1 16 3 V21 A3 3 0 0 1 13 24 H3 A3 3 0 0 1 0 21 V3 A3 3 0 0 1 3 0 Z
                                   M3.6 2.4 H12.4 A1.4 1.4 0 0 1 13.8 3.8 V6.2 A1.4 1.4 0 0 1 12.4 7.6 H3.6 A1.4 1.4 0 0 1 2.2 6.2 V3.8 A1.4 1.4 0 0 1 3.6 2.4 Z
                                   M2.1 11.3 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M6.5 11.3 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M10.9 11.3 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M2.1 15.5 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M6.5 15.5 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M10.9 15.5 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M2.1 19.7 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M6.5 19.7 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z
                                   M10.9 19.7 a1.5 1.5 0 1 0 3 0 a1.5 1.5 0 1 0 -3 0 Z" />
                        </svg>
                      </button>
                    }
                  </span>
                  }

                  <!-- ⚠️ A TOGGLE, not an action: it reads as pressed while on, and pressing it
                       again releases the row. aria-pressed says so to a screen reader, which a
                       colour change alone does not.

                       Filled mint while on, outlined while off -- the same pair this wizard uses
                       everywhere for chosen-versus-available.

                       🔴 ONE OF TWO, exactly as a cash row offers one of USE ALL / USE FOR
                       REMAINING: SELL ALL when the whole position still would not cover what is
                       outstanding, SELL FOR REMAINING when it would more than cover it. The store
                       decides, and a PRESSED button stays drawn whatever the arithmetic then says
                       -- otherwise it would vanish on its own click with no way to undo.

                       ⚠️ AND ONLY SELL ALL LOCKS THE BOX. The whole lot has no partial version to
                       adjust toward, so locking it says something true. SELL FOR REMAINING floors
                       to whole shares and therefore lands UNDER the target -- the advisor may
                       well need to nudge it up by one, so its box stays live (AndyP, 2026-09-24). -->
                  <!-- 🔴 THE LABEL TELLS THE TRUTH. When a single share costs more than is
                       outstanding it reads SELL 1 SHARE, because that is what pressing it does.
                       "Sell for remaining" promised a figure the row could not land on, and
                       `floor` returning 0 meant the button did nothing at all while still lighting
                       up as pressed (AndyP, 2026-09-24).

                       ⚠️ GONE once the distribution is covered, on every row that is not part of
                       what covered it. A disabled state was built first and looked worse than the
                       problem: a column of greyed SELL ALLs reads as a fault, and it buries the
                       rows that ARE contributing -- which keep their button, so the press can be
                       undone. -->
                  @if (store.showSellRemainingOnRow(i)) {
                    <button type="button"
                      class="button is-sm is-mint whitespace-nowrap"
                      [class.is-inverted]="store.rowFill(i) === 'none'"
                      [class.is-outlined]="store.rowFill(i) === 'none'"
                      [attr.aria-pressed]="store.rowFill(i) !== 'none'"
                      [title]="store.rowFill(i) !== 'none'
                        ? 'Covering what is left to distribute — press to clear this row'
                        : store.rowSellsOneShare(i)
                          ? 'One share is worth more than the amount still to distribute, so this sells a single share and the surplus stays in the account as cash'
                          : 'Sell as many whole shares as it takes to cover what is left, without going over'"
                      [attr.aria-label]="'Sell enough ' + p.ticker + ' to cover the remaining distribution'"
                      (click)="store.setRowSellRemaining(i)">
                      {{ store.rowSellsOneShare(i) ? 'SELL 1 SHARE' : 'SELL FOR REMAINING' }}
                    </button>
                  } @else if (store.showSellAllOnRow(i)) {
                    <!-- ⚠️ `whitespace-nowrap`, like the cash buttons. Without it the label broke
                         across two lines as "SELL / ALL" on a reallocation, where this column is
                         narrower than the distribution's (AndyP, 2026-09-25). -->
                    <button type="button"
                      class="button is-sm is-mint whitespace-nowrap"
                      [class.is-inverted]="!store.isRowSellAll(i)"
                      [class.is-outlined]="!store.isRowSellAll(i)"
                      [attr.aria-pressed]="store.isRowSellAll(i)"
                      [title]="store.isRowSellAll(i)
                        ? 'Selling the whole position — press to clear this row'
                        : 'Sell the whole position'"
                      [attr.aria-label]="'Sell the whole ' + p.ticker + ' position'"
                      (click)="store.setRowSellAll(i, !store.isRowSellAll(i))">
                      SELL ALL
                    </button>
                  }
                }
              </div>
            </td>
          </tr>
        }
      </tbody>
      </table>
    </div>
  </div>

  @if (store.isDistribution()) {
    <!-- This step's own contribution, and nothing more.

         ⚠️ It briefly also carried "Sourced $X of $25,000" with a signed delta, as the interim
         answer to not being able to see the running position. The strip above owns that now, and
         stating it in both places put the same figure on screen twice — the thing that made the
         funding step's validation message read as two separate problems. What belongs here is
         what THIS step raised; where that leaves the request is a whole-request fact. -->
    <div class="flex flex-col items-end pt-10 border-t gap-y-2 border-gray-lighter">
      <span class="text-sm text-gray">
        Raised on this step
        <span class="ml-10 font-semibold text-gray-darkest" style="font-variant-numeric: tabular-nums">
          {{ raisedHere() | currency }}
        </span>
      </span>
      <span class="text-xs text-gray">
        {{ store.soldPositions().length }} position{{ store.soldPositions().length === 1 ? '' : 's' }}
        marked to sell
      </span>
    </div>
  } @else {
    <p class="text-sm text-gray">
      {{ store.soldPositions().length }} position{{ store.soldPositions().length === 1 ? '' : 's' }} marked to
      sell · {{ store.sleevePositions().length }} remaining in account
    </p>
  }

  }
</div>
